import { SceneService } from '@src/services/SceneService';
import { deepClone } from '@src/utility/deepClone';
import debounce from 'lodash/debounce';
import { useCallback, useEffect, useMemo } from 'react';

import type { ILayoutSegment } from '@src/editor/models/layout/types';
import type { KaraokeConfig } from '@src/services/karaoke/karaokeTypes';
import type { SceneLayoutIndexStore } from '@src/stores-v2/SceneLayoutIndexStore';
import type { EditorStoreLike } from '@src/utility/compositionSettings';

interface UseKaraokeSceneTextPositionArgs {
  editorStore: EditorStoreLike;
  currentTime: number;
  basePosition: Pick<KaraokeConfig, 'x' | 'y' | 'x_alignment'>;
  preferredElementId?: string;
  sceneLayoutIndexStore?: SceneLayoutIndexStore;
}

const formatTextPositionValue = (
  value: string | number | null | undefined,
): string | undefined => {
  if (value === null || value === undefined) {
    return undefined;
  }

  if (typeof value === 'number') {
    return `${value}%`;
  }

  return value;
};

export function useKaraokeSceneTextPosition(
  args: UseKaraokeSceneTextPositionArgs,
) {
  const {
    editorStore,
    currentTime,
    basePosition,
    preferredElementId,
    sceneLayoutIndexStore,
  } = args;

  const sceneService = useMemo(() => new SceneService(), []);

  const karaokeSceneBinding = useMemo(() => {
    const editor = editorStore?.editor;
    if (!editor) {
      return null;
    }

    const timeline = editor.timeline;

    if (preferredElementId) {
      // 1. Try SceneLayoutIndexStore first (handles layout-generated elements
      //    whose IDs only exist in the renderer, not on the headless timeline).
      if (sceneLayoutIndexStore) {
        const resolvedId =
          sceneLayoutIndexStore.resolveVisibleRepresentationId(
            preferredElementId,
            currentTime,
          ) ?? preferredElementId;

        const record = sceneLayoutIndexStore.getRepresentationRoleRecordFor(
          resolvedId,
          currentTime,
        );

        if (record?.link?.sceneId) {
          const scene = timeline.findSceneById(record.link.sceneId);
          const binding = scene?.layout.bindings?.karaoke;

          if (scene && binding?.transformKeyframes?.length) {
            const keyframe = binding.transformKeyframes[0];
            return { scene, keyframe };
          }
        }
      }

      // 2. Fallback: SceneService path (for elements that live on the headless timeline)
      const sceneForPreferred = sceneService.findSceneForElement(
        timeline,
        preferredElementId,
        currentTime,
      );

      const binding = sceneForPreferred?.layout.bindings?.karaoke;

      if (sceneForPreferred && binding?.transformKeyframes?.length) {
        const keyframe = binding.transformKeyframes[0];
        return { scene: sceneForPreferred, keyframe };
      }
    } else {
      // Doesn't guarantee the most optimal scene is chosen, can pick a scene
      // from non-main track (e.g. b-roll)
      const sceneInfos = timeline.scenes
        .map(scene => {
          try {
            return sceneService.captureSceneInfoWithResolvedTimes(
              scene,
              timeline,
            );
          } catch (error) {
            return null;
          }
        })
        .filter((info): info is NonNullable<typeof info> => Boolean(info));

      const containingScene = sceneInfos.find(info => {
        const start = Math.min(info.sceneStartTime, info.sceneEndTime);
        const end = Math.max(info.sceneStartTime, info.sceneEndTime);
        return currentTime >= start && currentTime <= end;
      });

      const fallbackSceneWithKaraoke = sceneInfos.find(info =>
        Boolean(info.scene.layout.bindings?.karaoke),
      )?.scene;

      const targetScene =
        containingScene?.scene ??
        fallbackSceneWithKaraoke ??
        sceneInfos[0]?.scene;

      if (!targetScene) return null;

      const binding = targetScene.layout.bindings?.karaoke;
      const keyframe = binding?.transformKeyframes?.[0];

      if (!binding || !keyframe) {
        return null;
      }

      return { scene: targetScene, keyframe };
    }
  }, [
    currentTime,
    editorStore?.editor,
    editorStore?.editor?.timeline?.scenes,
    preferredElementId,
    sceneLayoutIndexStore,
    sceneService,
  ]);

  const textPositionOverride = karaokeSceneBinding?.keyframe.textPosition;

  const textPositionEffective = useMemo(
    () => ({
      x: textPositionOverride?.x ?? basePosition.x,
      y: textPositionOverride?.y ?? basePosition.y,
      x_alignment:
        textPositionOverride?.x_alignment ?? basePosition.x_alignment,
    }),
    [
      basePosition.x,
      basePosition.x_alignment,
      basePosition.y,
      textPositionOverride?.x,
      textPositionOverride?.x_alignment,
      textPositionOverride?.y,
    ],
  );

  const debouncedCommandUpdateScene = useMemo(() => {
    if (!editorStore?.editor) return null;

    const debounced = debounce(
      (sceneId: string, layoutJson: ILayoutSegment) => {
        editorStore.editor.commandUpdateScene(sceneId, { layout: layoutJson });
      },
      300,
    );

    return debounced;
  }, [editorStore?.editor]);

  useEffect(() => {
    return () => {
      debouncedCommandUpdateScene?.cancel();
    };
  }, [debouncedCommandUpdateScene]);

  const updateTextPosition = useCallback(
    (
      propertyName: 'x' | 'y' | 'x_alignment',
      value: string | number | null | undefined,
      fallbackUpdate?: (propertyMap: Partial<KaraokeConfig>) => void,
    ) => {
      const formatted = formatTextPositionValue(value);

      if (karaokeSceneBinding?.keyframe.textPosition && editorStore?.editor) {
        // Scene has per-scene override — update scene's textPosition
        const layoutJson = deepClone<ILayoutSegment>(
          karaokeSceneBinding.scene.layout.toJSON(),
        );
        const karaokeBinding = layoutJson.bindings?.karaoke;

        if (!karaokeBinding?.transformKeyframes?.length) {
          return;
        }

        const keyframe = karaokeBinding.transformKeyframes[0];
        keyframe.textPosition = { ...keyframe.textPosition };

        if (formatted === undefined) {
          delete keyframe.textPosition[propertyName];
        } else {
          keyframe.textPosition[propertyName] = formatted;
        }

        if (debouncedCommandUpdateScene) {
          debouncedCommandUpdateScene(karaokeSceneBinding.scene.id, layoutJson);
        } else {
          editorStore.editor.commandUpdateScene(karaokeSceneBinding.scene.id, {
            layout: layoutJson,
          });
        }

        return;
      }

      // No override — update global KaraokeConfig
      fallbackUpdate?.({ [propertyName]: formatted });
    },
    [editorStore?.editor, karaokeSceneBinding, debouncedCommandUpdateScene],
  );

  const hasSceneOverride = !!karaokeSceneBinding?.keyframe.textPosition;

  const setSceneOverride = useCallback(
    (enabled: boolean) => {
      if (!karaokeSceneBinding || !editorStore?.editor) return;

      const layoutJson = deepClone<ILayoutSegment>(
        karaokeSceneBinding.scene.layout.toJSON(),
      );
      const karaokeBinding = layoutJson.bindings?.karaoke;
      if (!karaokeBinding?.transformKeyframes?.length) return;

      const keyframe = karaokeBinding.transformKeyframes[0];

      if (enabled) {
        // Toggle ON: copy global config → scene's textPosition (create override)
        keyframe.textPosition = {
          x: formatTextPositionValue(basePosition.x),
          y: formatTextPositionValue(basePosition.y),
          x_alignment: formatTextPositionValue(basePosition.x_alignment),
        };
      } else {
        // Toggle OFF: delete scene override → scene inherits global config
        delete keyframe.textPosition;
      }

      editorStore.editor.commandUpdateScene(karaokeSceneBinding.scene.id, {
        layout: layoutJson,
      });
    },
    [editorStore?.editor, karaokeSceneBinding, basePosition],
  );

  const sceneCount = editorStore?.editor?.timeline?.scenes?.length ?? 0;

  return {
    textPositionEffective,
    updateTextPosition,
    hasSceneOverride,
    setSceneOverride,
    sceneCount,
  };
}
